import { Directive, OnInit, Input, ElementRef, Renderer2, AfterViewInit, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';
import { FadeInOnIntersectingDirection, FadeInOnIntersectingService } from './fade-in-on-intersecting.service';

@Directive({
    selector: '[fadeInOnIntersecting]'
})
export class FadeInOnIntersectingDirective implements AfterViewInit {

    private intersectionObserver?: IntersectionObserver;

    @Output() public elementIntersecting: EventEmitter<IntersectionObserverEntry> = new EventEmitter();

    @Input() public fadeInOnIntersecting: boolean = true;
    @Input() public delay: number = 200;
    @Input() public duration: number = 500;
    @Input() set direction(direction: string) { if(FadeInOnIntersectingDirection[direction.toUpperCase()]) this.directionEnum = FadeInOnIntersectingDirection[direction.toUpperCase()] };
    @Input() set fadeInNow(bool: boolean) {
        if(bool) this.animateEntry();
    }

    private directionEnum: FadeInOnIntersectingDirection = FadeInOnIntersectingDirection.UP

    // Docs: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
    @Input() public options: IntersectionObserverInit = {
        root: null, // The element that is used as the viewport for checking visibility of the target. Must be the ancestor of the target. Defaults to the browser viewport if not specified or if null.
        rootMargin: '0px', // Margin around the root. Can have values similar to the CSS margin property, e.g. "10px 20px 30px 40px" (top, right, bottom, left). The values can be percentages. This set of values serves to grow or shrink each side of the root element's bounding box before computing intersections. Defaults to all zeros.
        threshold: 0.5 // Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed. If you only want to detect when visibility passes the 50% mark, you can use a value of 0.5. If you want the callback to run every time visibility passes another 25%, you would specify the array [0, 0.25, 0.5, 0.75, 1]. The default is 0 (meaning as soon as even one pixel is visible, the callback will be run). A value of 1.0 means that the threshold isn't considered passed until every pixel is visible.
    }


    constructor(
        private element: ElementRef,
        private renderer: Renderer2,
        private fadeInOnIntersectingService: FadeInOnIntersectingService
    ) {

    }

    ngAfterViewInit() {
        // Skip (non existing) intersection observer during SSR (prerender)
        if(!IntersectionObserver) return;

        this.intersectionObserver = new IntersectionObserver(entries => {
            this.checkForIntersection(entries);
        }, this.options);

        this.intersectionObserver.observe(<Element>this.element.nativeElement);
        this.setStyle('opacity', '0');
    }

    private checkForIntersection(entries: Array<IntersectionObserverEntry>) {
        entries.forEach((entry: IntersectionObserverEntry) => {
            if(this.checkIfIntersecting(entry)) {
                this.animateEntry(); // Animate the element 
                this.elementIntersecting.emit(entry); // Tell parents about the intersection 
            }
        })
    }

    private animateEntry() {
        // Animate the element using service (to enable all elements to come in after each other, i.e. not simultaneously)
        this.fadeInOnIntersectingService.animateEntry({
            elementRef: this.element,
            direction: this.directionEnum,
            delay: this.delay,
            duration: this.duration
        });

        this.intersectionObserver.unobserve(<Element>this.element.nativeElement); // Unobserve for performance
        this.intersectionObserver.disconnect(); // Disconnect for perfomance
    }

    private checkIfIntersecting(entry: IntersectionObserverEntry) {
        return (<any>entry).isIntersecting && entry.target === this.element.nativeElement;
    }

    private setStyle(style: string, value: string) {
        if(!this.element) return console.log("no element to fade in?")
        this.renderer.setStyle(this.element.nativeElement, style, value);
    }

}
